import type { ViteReactSSGContext } from 'vite-react-ssg';

/**
 * How long hydration may wait for the router before it goes ahead regardless.
 * Normally the wait is one small request; this only bounds a request that never
 * settles. Running out falls back to the library's own behaviour — the page drawn
 * twice — so the bound is generous: a page that becomes interactive a few seconds
 * late is better than one that stays doubled.
 */
export const ROUTER_READY_TIMEOUT_MS = 10_000;

/**
 * Hydrate only once the router has finished its first navigation (client only).
 * Passed to `ViteReactSSG` as its setup callback in `main.tsx`.
 *
 * On a prerendered page vite-react-ssg 0.9 gives EVERY route an asynchronous
 * loader (`transformStaticLoaderRoute`): it fetches
 * `static-loader-data-manifest-<hash>.json` — an empty `{}` on this site, which
 * has no loaders of its own. The router therefore starts with
 * `initialized: false`, and until the loaders settle `RouterProvider` renders the
 * route's `HydrateFallback` — `null` here — instead of the page. The library
 * hydrates as soon as `react-dom/client` has loaded; when that wins the race (a
 * warm cache makes the scripts instant while the manifest still costs a request),
 * React hydrates `null` against the prerendered markup, cannot adopt it, and
 * mounts a second, live copy of the page after it. The visitor sees the first,
 * dead copy: two headers and footers, the page twice as tall, and no header
 * control that responds — Resources, Features, the language menu, the burger.
 * React Router's "No `HydrateFallback` element provided" warning marks every
 * such load. A cold load hides it, because there the scripts are the slow part,
 * which is why the defect survived every first-visit check.
 *
 * `ViteReactSSG` awaits this callback after it has created the router and before
 * it calls `hydrateRoot`, so waiting here for `router.state.initialized` makes the
 * first client render the real page — the same tree as the prerendered markup —
 * whatever order the requests finish in. The router also settles when a loader
 * fails (a missing manifest becomes a route error), so the wait ends either way.
 * During the prerender there is no router and nothing to wait for.
 *
 * Earlier attempts moved markup around inside `#root`; they were measured on cold
 * loads only and changed nothing. This removes the race itself.
 */
export async function hydrateWhenRouterReady(
  { router, isClient }: Pick<ViteReactSSGContext, 'router' | 'isClient'>,
  timeoutMs: number = ROUTER_READY_TIMEOUT_MS,
): Promise<void> {
  if (!isClient || !router || router.state.initialized) return;
  let unsubscribe = (): void => {};
  let timer: ReturnType<typeof setTimeout> | undefined;
  try {
    await new Promise<void>((resolve) => {
      timer = setTimeout(resolve, timeoutMs);
      unsubscribe = router.subscribe((state) => {
        if (state.initialized) resolve();
      });
      // Settled between the check above and the subscription.
      if (router.state.initialized) resolve();
    });
  } finally {
    clearTimeout(timer);
    unsubscribe();
  }
}
